import * as Debug from 'debug';
import { Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2, ViewChild} from '@angular/core';
import { isPlatformBrowser} from '@angular/common';
import { ActivatedRoute, Router} from '@angular/router';
import { NoteService} from '../shared/services/note_service';
import { ModalDismissReasons, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import { UserService} from '../shared/services/user_service';
import { StreamService} from '../shared/services/stream_service';
import { SearchService} from '../shared/services/search_service';
import { Util} from '../shared/util/util';

import {Note} from '../shared/types/note';
import { Stream } from '../shared/types/stream';
import { CategoriesService } from '../shared/services/categories_service';
import { CacheService } from '../shared/services/cache_service';
import { forkJoin } from 'rxjs';

const debug = Debug('notd:Landing');

export interface StreamExtType extends Stream {
  channels: Stream[];
  debug: Record<string, unknown>;
  total: number;
}

@Component({
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss']
})
export class LandingComponent implements OnInit, OnDestroy {
  @ViewChild('streamsList', {static: false}) public streamsList: any;

  public isCollapsedSearch: boolean;
  public closeResult: string;

  public featuredStreams: any[] = [];
  public recentStreams: any[] = [];
  public featuredChannels: any[] = [];
  public isLoggedIn = false;
  public user: any = {};

  isMobile: {value: boolean; size?: string} = {
    value: false
  };
  streamMobileWidth: any;
  streamMobileSliderPos = 0;
  selectedSlide = 0;
  loadingFeaturedChannels = false;
  allowOwnStream = false;
  latestPostsSliderData = [];
  streamStreamCategory = 'popular';
  overTheTop = false;
  touchStart;
  moveSliderDirection: any;

  public streamTopListType = 'newest';
  public latestChannels: Stream[];
  public categoriesData: any = [];
  public readMorePositions: {category: string; position: number | string; bgColor?: string}[] = [];
  public showSearchBarOnMobile: boolean;
  public mobileContent = {
    type: 'latest',
    notes: [],
    offset: 0,
    size: 40,
    showMoreBtn: true,
    loading: {
      global: false,
      loadMore: false
    }
  };

  public showFeaturedNotesLimit = 2;
  public showNewestChannelsLimit = 12;

  public desktopContentSort: {offset: number; size: number; ended: boolean} = {
    offset: 0,
    size: 10,
    ended: false
  };

  public featuredNotes: {notes: Note[]; offset: [number, number]} = {
    notes: [],
    offset: [0, this.showFeaturedNotesLimit]
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  searchFormProps: {} = {
    customClass: 'search-holder',
    showIcon: true,
    iconSide: 'left',
    showButton: false,
    inputPlaceholder: 'Search our notes',
    buttonText: 'Want to write your own streams?'
  };

  private featuredSubscription;
  private getSearchParamsChangeSubscription;
  private getChannelsSubscription;
  private userStatusChange;
  private resizeInterval;

  private loadMoreFromNewest = {
    size: 60,
    offset: 0
  };

  constructor(private noteService: NoteService,
              private modalService: NgbModal,
              private userService: UserService,
              private streamService: StreamService,
              private searchService: SearchService,
              private cacheService: CacheService,
              private categoriesService: CategoriesService,
              private renderer: Renderer2,
              // eslint-disable-next-line @typescript-eslint/ban-types
              @Inject(PLATFORM_ID) private platformId: Object,
              private util: Util,
              private router: Router, private route: ActivatedRoute) {
  }

  @HostListener('window:scroll', ['$event']) controlSlideArrow() {
    const topOffset = window.pageYOffset || document.documentElement.scrollTop;
    this.overTheTop = topOffset > 40 ? true : false;
  }

  public ngOnInit(): void {
    this.user = this.route.snapshot.data.user;
    this.mobileContent.type = this.route.snapshot.data.mode ? this.route.snapshot.data.mode : 'latest';
    this.isLoggedIn = (this.user && this.user.lastName) ? true : false;

    this.userStatusChange = this.userService.curUser$.subscribe(change => {
      this.isLoggedIn = this.userService.isLoggedIn();
    });

    this.getSearchParamsChangeSubscription = this.searchService.getSearchParamsChange().subscribe(() => {
      this.searchForStreams();
    }, err => {
      console.log(err);
    });

    if (isPlatformBrowser(this.platformId)) {
      this.isMobile.value = this.util.isTouchDevice();
      this.isMobile.size = window.innerWidth < 736 ? 'sm' : '';

      window.addEventListener('resize', () => {
        clearTimeout(this.resizeInterval);
        this.resizeInterval = setTimeout(() => {
          this.isMobile.size = window.innerWidth < 736 ? 'sm' : '';
        }, 550);
      }, false);

      window.addEventListener('orientationchange', () => {
          this.loadLandingPageContent();
      }, false);
    }

    this.getCategories();
    this.loadLandingPageContent();

    if (this.isLoggedIn) {
      this.checkOwnStreamAvailable();
    }

    this.readMorePositions = [
      {
        category: 'ideas',
        position: 2,
      },
      {
        category: 'news',
        position: 6,
      },
      {
        category: 'commentary',
        position: 9,
      },
      {
        category: 'opinion',
        position: 13,
      }
    ];

  }

  checkOwnStreamAvailable() {
    this.streamService.getUserStreams().then((streams) => {
      this.allowOwnStream = (this.userService.isLoggedIn() && streams.length === 0) ? true : false;
    }, err => {
      console.log(err);
    })
    .catch(err => {
      console.log(err);
      throw err;
    });
  }

  public loadLandingPageContent(): void {
    this.resetLoadingContentParams();

    if (
      !this.isMobile.value ||
      (this.isMobile.value && this.isMobile.size !== 'sm')) {
      this.getLandingPageContent();
    } else {
      // load LP content for mobile, different type of content
      if (this.mobileContent.type === 'latest') {
        this.loadMobileContent();
      } else {
        this.mobileContent.loading.global = true;
        this.getLandingPageContent();
      }
    }
  }

  public loadMobileContent(): void {
    this.mobileContent.offset = 0;
    this.mobileContent.loading.global = true;
    const query = {
      size: this.mobileContent.size.toString(),
      fromOffset: '0',
      getDetails: '1'
    };

    this.searchService.getPosts(query).subscribe(response => {
      const notes = response.posts.filter(p => !p.trashed);
      if (notes && notes.length) {
        this.updateNotesByUserProfile(notes).then(results => {
          this.mobileContent.notes.concat(results);
          this.mobileContent.loading.loadMore = false;
        });
      }

      this.mobileContent.notes = notes;
      this.mobileContent.loading.global = false;
    }, err => {
      console.error('Error while searching for the posts', err);
    });

    this.noteService.getFeaturedNotes(0, this.showFeaturedNotesLimit).subscribe((featuredNotes: Note[]) => {
      this.featuredNotes.notes = featuredNotes.filter(n => !n.trashed);
    }, err => {
      console.error('There was an error while getting the featured notes', err);
    });
  }

  public loadMoreMobileContent(): void {
    this.mobileContent.loading.loadMore = true;
    this.mobileContent.offset = this.mobileContent.offset + this.mobileContent.size;

    const query = {
      size: this.mobileContent.size.toString(),
      fromOffset: this.mobileContent.offset.toString(),
      getDetails: '1'
    };

    this.searchService.getPosts(query).subscribe(response => {
      const notes = response.posts.filter(p => !p.trashed);

      if (notes && notes.length) {
        this.updateNotesByUserProfile(notes).then(results => {
          this.mobileContent.notes = this.mobileContent.notes.concat(results);
        });
      }

      this.mobileContent.loading.loadMore = false;
      this.mobileContent.showMoreBtn = notes.length >= this.mobileContent.size;
    }, err => {
      console.error('Error while searching for the posts', err);
    });
  }

  loadLatestPosts() {
    const streamsArray = [];
    const notesLimit = this.isMobile.value ? 2 : 3;

    const query = {
      sort: 'createdDateDesc',
      size: notesLimit // reduce max size of response to  optimize speed
    };

    for (const stream of this.featuredChannels) {
      if (stream.latestPostId) {
        streamsArray.push(this.streamService.getStreamNotes(stream.channelId, query));
      }
    }

    Promise.all(streamsArray).then(streamNotes => {
      const notesArray = [];
      if (streamNotes && streamNotes.length) {
        for (let i = 0; i < streamNotes.length; i++) {
          const notesObj = streamNotes[i]['posts'].filter(n => n.state !== 'HIDDEN');
          const latestNotes = notesObj ? notesObj.sort((a, b) => Number(b.created) - Number(a.created)).slice(0, notesLimit) : [];
          notesArray.push(latestNotes);
          Object.assign(this.featuredChannels[i] , {
            latestPosts: latestNotes
          });
        }
      }

    }, err => {
      console.log('Error while getting stream notes!', err);
    });
  }

  loadMoreFeaturedChannels(): void {
    let fullChannelsList = [];
    const channelIds = [];

    for (const ch of this.featuredChannels) {
      channelIds.push(ch.id);
    }

    this.noteService.getNewestChannels(this.loadMoreFromNewest.offset, this.loadMoreFromNewest.size).subscribe(newChannels => {
      if (!newChannels.length) {
        this.desktopContentSort.ended = true;
      }

      fullChannelsList = [...this.featuredChannels, ...newChannels];
      const newStreamIds = [];

      for (const ch of newChannels) {
        channelIds.push(ch.id);
        newStreamIds.push(ch.id);
      }

      const uniqueStreamIds = Array.from(new Set(channelIds));

      const streamsIdsOutOfFeatured = [];
      for (const nId of newStreamIds) {
        let lengthRes = 0;
        for (const ch of this.featuredChannels) {
          if (ch.id === nId) {
            lengthRes++;
          }
        }

        if (!lengthRes) {
          streamsIdsOutOfFeatured.push(nId);
        }
      }
      const newChannelsList = [];
      for (const ch of fullChannelsList) {
        for (const cId of streamsIdsOutOfFeatured) {
          if (ch.id === cId) {
            newChannelsList.push(ch);
            this.getStreamLatestPosts(ch);
          }
        }
      }

      this.featuredChannels = this.featuredChannels.concat(newChannelsList);
      this.loadMoreFromNewest.offset = this.loadMoreFromNewest.offset + this.loadMoreFromNewest.size;
    });
  }

  getStreamLatestPosts(stream: Stream & {latestPosts: any[]}): void {
    const notesLimit = this.isMobile.value ? 2 : 3;

    const query = {
      sort: 'createdDateDesc',
      size: (notesLimit * 2)  // reduce max size of response to  optimize speed
    };

    this.streamService.getStreamNotes(stream.id, query).then(notes => {
      const notesArray = [];
      const notesObj = notes['posts'].filter(n => n.state !== 'HIDDEN');
      const latestNotes = notesObj ? notesObj.sort((a, b) => Number(b.created) - Number(a.created)).slice(0, notesLimit) : [];
      notesArray.push(latestNotes);

      Object.assign(stream, {
        latestPosts: latestNotes
      });

    });
  }

  resetLoadingContentParams(): void {
    this.loadMoreFromNewest = {
      size: 60,
      offset: 0
    };

    this.desktopContentSort = {
      offset: 0,
      size: 10,
      ended: false,
    };
  }

  searchForStreams() {
    this.featuredChannels = [];
    this.loadingFeaturedChannels = true;
    const queryMediaTypeTags = this.searchService.searchParams['mediaTag'] ? this.searchService.searchParams['mediaTag'] : '';

    this.getChannelsSubscription = this.searchService.getChannels().subscribe( res => {
      const streams = res.channels;
      this.featuredChannels = streams;
      this.loadLatestPosts();
      this.loadingFeaturedChannels = false;

      if (streams.length > 0 && this.isLoggedIn) {
          this.streamService.getUserStreams().then(
              myStreams => {
                  debug('searchForStreams', myStreams);

                  if (!myStreams) {
                    return;
                  }
                  streams.forEach(fs => {
                      const myStream = myStreams.filter(s => s.id === fs.id);
                      if (myStream.length > 0) {
                          fs._owned = true;
                      }
                  });
              },
              err => {
                console.error('Error while fetching user streams', err);
              }
          )
          .catch(err => {
            console.log(err);
            throw err;
          });
      }
    }, err => {
      console.log(err);
    });
  }

  public searchBarMobileVisibleStatus(ev: boolean | null): void {
    this.showSearchBarOnMobile = ev;
  }

  signin() {
    debug('signin');
  }

  readMore(stream: any) {
    if (stream._owned) {
      this.router.navigate(['/user', this.user.id, 'streams', stream.id, stream.name]);
    } else {
      this.router.navigate(['/streams', stream.id, 'view', stream.name]);
    }
  }

  open(content) {
    this.modalService.open(content).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
    });
  }

  isLandingPageHeadingHided() {
    if (isPlatformBrowser(this.platformId)) {
      return this.cacheService.cache.get('isLandingPageHeadingHided');
    } else {
      return false;
    }
  }

  hideHeading() {
    if (isPlatformBrowser(this.platformId)) {
      this.cacheService.setCache('isLandingPageHeadingHided', true);
    } else {
    }
  }

  moveLatestPostsSlider(channelId: string, direction: string) {
    this.featuredChannels.map(stream => {
      if (stream.channelId === channelId) {
        if (direction === 'right') {
          if (stream.sliderCounter + 1 < stream.latestPosts.length) {
              stream.sliderCounter++;
          }
        } else {
          if (stream.sliderCounter > 0) {
              stream.sliderCounter--;
          }
        }
      }
    });
  }

  createMobileStreamSlider(state: boolean) {
    const browserWidth = document.body.offsetWidth;

    this.selectedSlide = 0;
    this.streamMobileWidth = (state) ? browserWidth - 40 : '';
  }

  urlifyName(title: string) {
      return this.noteService.urlifyName(title);
  }

  streamSelector(selected: string) {
    this.streamStreamCategory = selected;

    this.selectedSlide = 0;
  }

  navigateToNote(noteId: any, noteName: string) {
    if (noteId && noteName) {
      this.router.navigate(['/notes', noteId, this.urlifyName(noteName)]);
    }
  }

  toggleSideMenu() {
    this.util.toggleSideMenu('slide');
  }

  ngOnDestroy() {
    if (this.featuredSubscription) {
      this.featuredSubscription.unsubscribe();
    }

    if (this.getSearchParamsChangeSubscription) {
      this.getSearchParamsChangeSubscription.unsubscribe();
    }

    if (this.getChannelsSubscription) {
      this.getChannelsSubscription.unsubscribe();
    }

    if (this.userStatusChange) {
      this.userStatusChange.unsubscribe();
    }
  }

  private getLandingPageContent(): void {
    forkJoin<[StreamExtType, Stream[], Note[]]>([
      this.noteService.getFeaturedChannels(this.desktopContentSort.offset, this.desktopContentSort.size),
      this.noteService.getNewestChannels(0, 12),
      this.noteService.getFeaturedNotes(0, this.showFeaturedNotesLimit)
    ]).subscribe({
      next: ([featuredStreams, newStreams, featuredNotes]) => {
        const featureStreams = featuredStreams.channels.filter(s => !s.trashed);
        const newestStreams = newStreams.filter(s => !s.trashed);
        this.featuredNotes.notes = featuredNotes;
        this.latestChannels = newestStreams ? newestStreams.slice(0, this.showNewestChannelsLimit) : [];
        const featured = [];
        for (const fs of featureStreams) {
          if (!this.latestChannels.filter(s => s.id === fs.id).length) {
            featured.push(fs);
          }
        }

        this.featuredChannels = featured;
        this.noteService.featuredStreams$.next(featured);
        this.loadLatestPosts();
        this.loadingFeaturedChannels = false;

        for (const stream of featured) {
          stream.gridType = this.randomNumber(1, 4);
        }

        if (featured.length > 0 && this.isLoggedIn) {
          this.streamService.getUserStreams().then(
            myStreams => {
              featured.forEach(fs => {
                const myStream = myStreams.filter(s => s.id === fs.id);
                if (myStream.length > 0) {
                    fs._owned = true;
                }
              });
            },
            err => {
              console.log(err);
            }
          ).catch(err => {
            console.log(err);
            throw err;
          });
        }

        if (this.isMobile.value && this.isMobile.size === 'sm') {
          this.mobileContent.loading.global = false;
        }
      },
      error: error => {
        if (this.isMobile.value && this.isMobile.size === 'sm') {
          this.mobileContent.loading.global = false;
        }
        console.log(error);
      },
      complete: () => {
        console.log('Completed');
      }
    });
  }

  private updateNotesByUserProfile(notes: Note[]): Promise<Note[] | []> {
    const userProfilesId = [];

    for (const n of notes) {
      if (n.userId) {
        userProfilesId.push(n.userId);
      }
    }

    const uniqueUsersId = Array.from(new Set(userProfilesId));
    const getProfileApiCallArray = [];

    for (const uId of uniqueUsersId) {
      getProfileApiCallArray.push(this.userService.getUserProfile(uId));
    }

    if (getProfileApiCallArray && getProfileApiCallArray.length) {
      return Promise.all(getProfileApiCallArray).then(results => {
        const usersResult = results;

        for (const n of notes) {
          for (const u of usersResult) {
            if (u.id === n.userId) {
              n.user = u;
            }
          }
        }

        return Promise.resolve(notes);
      }, err => {
        console.log('Error while getting user profiles!', err);
        // return Promise.resolve(notes);
        return Promise.resolve(notes);
      });
    } else {
      return Promise.resolve([]);
    }
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return  `with: ${reason}`;
    }
  }


  private randomNumber(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  private setTags(tags: string[]): string[] {
    const tagsList = [];
    tags.map(tag => {
      const splitTag: any[] = tag.split('-');

      if (splitTag[0] === 'form' || splitTag[0] === 'category') {
        tag = (splitTag.length > 2) ? `${splitTag[1].replace('-', '')} ${splitTag[2]}` : splitTag[1].replace('-', '');
        tagsList.push(tag);
      }
    });

    return tagsList;
  }

  private getCategories() {
    this.categoriesData = this.categoriesService.getStructure();
  }
}
